import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { BeaconSnapshot, Task } from '@/api';
import { useWebsocket } from '@/common/providers/websocket-provider';
import { useFormatDate } from '@/common/hooks';
import { _TaskBeacons } from './models';
import { TaskBeaconSnapshotContext } from './context';

type TaskBeaconSnapshotProviderProps = {
    task: Task;
    readonly children?: ReactNode;
};
export function TaskBeaconSnapshotProvider(
    props: TaskBeaconSnapshotProviderProps
): ReactElement {
    const { task, children } = props;
    const { formatSmartShortTimeAndDate } = useFormatDate();

    const taskBeacons = useMemo<_TaskBeacons>(() => {
        return {
            beacons: task.beaconSnapshot,
            updatedAt: task.beaconsUpdatedAt
                ? new Date(task.beaconsUpdatedAt)
                : null
        };
    }, [task.beaconSnapshot, task.beaconsUpdatedAt]);

    const [socketBeacons, setSocketBeacons] = useState<_TaskBeacons>({
        beacons: [],
        updatedAt: null
    });

    const { subscribe, unsubscribe, on, off } = useWebsocket();

    useEffect(() => {
        const ids = subscribe([
            { resource: `companies#${task.companyId}.tasks_beacons` }
        ]);
        return () => {
            unsubscribe(ids);
        };
    }, [task, subscribe, unsubscribe]);

    useEffect(() => {
        const callback = (message: unknown) => {
            const { data } = message as {
                data: {
                    beaconSnapshot: BeaconSnapshot[];
                    beaconsUpdatedAt: string;
                };
            };

            setSocketBeacons({
                beacons: data.beaconSnapshot,
                updatedAt: new Date(data.beaconsUpdatedAt)
            });
        };
        on('tasks_beacons_v3', callback);

        return () => {
            off('tasks_beacons_v3', callback);
        };
    }, [on, off, setSocketBeacons]);

    const { beacons, updatedAt } = useMemo<_TaskBeacons>(() => {
        if (taskBeacons.updatedAt && socketBeacons.updatedAt) {
            if (taskBeacons.updatedAt > socketBeacons.updatedAt) {
                return taskBeacons;
            } else {
                return socketBeacons;
            }
        } else if (taskBeacons.updatedAt) {
            return taskBeacons;
        } else if (socketBeacons.updatedAt) {
            return socketBeacons;
        } else {
            return {
                beacons: [],
                updatedAt: null
            };
        }
    }, [taskBeacons, socketBeacons]);

    const updatedAtStr = useMemo<string | null>(() => {
        if (!updatedAt) {
            return null;
        } else {
            return formatSmartShortTimeAndDate(updatedAt);
        }
    }, [formatSmartShortTimeAndDate, updatedAt]);

    return (
        <TaskBeaconSnapshotContext.Provider
            value={{
                beacons,
                updatedAt,
                updatedAtStr
            }}
        >
            {children}
        </TaskBeaconSnapshotContext.Provider>
    );
}
